import { Component, Input, OnInit } from '@angular/core';
import { FormGroupType } from '@app/core/utils/formgroup-type';
import {
  AssetType,
  ConnectionType,
  CreateMeteringPointCommand,
  MeteringPointType,
  NetSettlementGroup,
  UpdateMeteringPointCommand
} from '@app/api/dmc';
import { ReactiveFormsModule } from '@angular/forms';
import { GuideCardComponent } from '@shared/components/guide-card/guide-card.component';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { FormConfig } from '@shared/interfaces/form-configs/form-config';
import { meteringPointTypeConfig } from '@views/meteringpoint/configs/meteringpointtype-config';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'app-meteringpoint-form-production',
  imports: [
    ReactiveFormsModule,
    GuideCardComponent,
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    SelectDirective,
    TranslatePipe,
  ],
  templateUrl: './meteringpoint-form-production.component.html',
  styleUrl: './meteringpoint-form-production.component.css'
})
export class MeteringpointFormProductionComponent implements OnInit {
  @Input() public form!: FormGroupType<CreateMeteringPointCommand | UpdateMeteringPointCommand>;

  @Input() public fullWidth: boolean = true;

  @Input() public edit: boolean = false;

  /** Configs **/
  public configs: Partial<Record<MeteringPointType, FormConfig<CreateMeteringPointCommand>>> = meteringPointTypeConfig;

  /** Populated Lists **/
  public netSettlementGroupList: KeyValue[] = enumToKeyValue(NetSettlementGroup, {
    prefixValue: 'enum.meteringpoint.netsettlementgroup.',
    omitKeys: ['NotSpecified', 'NetSettlementGroup1', 'NetSettlementGroup2', 'NetSettlementGroup4', 'NetSettlementGroup5', 'NetSettlementGroup6', 'NetSettlementGroup99']
  });
  public connectionTypeList: KeyValue[] = enumToKeyValue(ConnectionType, {
    prefixValue: 'enum.meteringpoint.connectiontype.',
    omitKeys: ['NotSpecified']
  });
  public assetTypeList: KeyValue[] = enumToKeyValue(AssetType, {
    prefixValue: 'enum.meteringpoint.assettype.',
    omitKeys: ['NotSpecified']
  });

  private readonly baseOmitKeys: (keyof typeof NetSettlementGroup)[] = ['NotSpecified', 'NetSettlementGroup1', 'NetSettlementGroup2', 'NetSettlementGroup4', 'NetSettlementGroup5', 'NetSettlementGroup6', 'NetSettlementGroup99'];

  public rebuildNetSettlementGroupList(): void {
    const currentNetSettlementGroup = this.form.get('detailCharacteristics.netSettlementGroup')?.value as keyof typeof NetSettlementGroup | null;
    const omitKeys = this.baseOmitKeys.filter(key => key !== currentNetSettlementGroup);
    this.netSettlementGroupList = enumToKeyValue(NetSettlementGroup, {
      prefixValue: 'enum.meteringpoint.netsettlementgroup.',
      omitKeys: omitKeys as (keyof typeof NetSettlementGroup)[]
    });
  }

  public ngOnInit(): void {

    /** Net Settlement Group **/
    this.form.get('detailCharacteristics.netSettlementGroup')?.valueChanges
      .pipe(startWith(this.form.get('detailCharacteristics.netSettlementGroup')?.value))
      .subscribe((netSettlementGroup: NetSettlementGroup) => {
        const meteringPointType = this.form.get('detailCharacteristics.meteringPointType')?.value as MeteringPointType;

        const connectionType = 'detailCharacteristics.connectionType';
        const assetType = 'detailCharacteristics.assetType';
        const capacity = 'detailCharacteristics.capacity';
        const powerPlantIdentification = 'detailCharacteristics.powerPlantIdentification';

        const fields = [
          connectionType,
          assetType,
          capacity,
          powerPlantIdentification
        ];

        if (!netSettlementGroup || netSettlementGroup === NetSettlementGroup.NotSpecified) {
          fields.forEach(field => this.disableFieldIfNotRequired(meteringPointType, field));
        } else if (netSettlementGroup === NetSettlementGroup.NetSettlementGroup0) {
          // AssetType, Capacity and PowerPlantIdentification are optional when NetSettlementGroup0 is set
          this.form.get(assetType)?.enable();
          this.form.get(capacity)?.enable();
          this.form.get(powerPlantIdentification)?.enable()
          // ConnectionType is not allowed when NetSettlementGroup0 is set
          this.disableFieldIfNotRequired(meteringPointType, connectionType);
        } else {
          fields.forEach(field => this.form.get(field)?.enable());
        }
      })
  }

  private isFieldRequired(type: MeteringPointType, field: string): boolean {
    // null/undefined is the initial state before a type is selected — not an error
    if (!type) return false;

    // 1. Check if configs exist for this type
    const config = this.configs[type] as Record<string, { state: string } | undefined> | undefined;

    if (!config) {
      return false;
    }

    const cleanField = field.trim();
    const fieldConfig = config[cleanField];

    if (!fieldConfig) {
      return false;
    }

    return fieldConfig.state === 'required';
  }

  private disableFieldIfNotRequired(type: MeteringPointType, field: string): void {
    if (!this.isFieldRequired(type, field)) {
      this.form.get(field)?.disable();
    }
  }
}
